import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { LocalTime } from '@js-joda/core'
import { BehaviorSubject, Observable } from 'rxjs'
import {
  IScheduleTimeSlotDialogOptions,
  TimeScheduleSlotDialogComponent,
} from '../time-schedule-slot-dialog/time-schedule-slot-dialog.component'
import { TimeScheduleSlotComponent } from '../time-schedule-slot/time-schedule-slot.component'
import { DaySchedule, TimeSchedule } from '../work-schedule'

@Component({
  selector: 'app-daily-schedule-edit',
  templateUrl: './daily-schedule-edit.component.html',
  styleUrls: ['./daily-schedule-edit.component.scss'],
  imports: [TimeScheduleSlotComponent, MatIconButton, MatIcon, AsyncPipe],
})
export class DailyScheduleEditComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private _daySchedule: DaySchedule

  public readonly timeSlots$ = new BehaviorSubject<TimeSchedule[]>([])

  private matDialog = inject(MatDialog)

  @Input()
  public name: string

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set daySchedule(daySchedule: DaySchedule) {
    this._daySchedule = daySchedule
    this.timeSlots = daySchedule?.timeSchedules
      ? this.sortByStartingTime(daySchedule.timeSchedules)
      : []
  }

  private set timeSlots(slots: TimeSchedule[]) {
    const sortedSlots = this.sortByStartingTime(slots)
    this.timeSlots$.next(sortedSlots)

    this._daySchedule.timeSchedules = sortedSlots
  }

  private get timeSlots(): TimeSchedule[] {
    return this.timeSlots$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public addTimeSlot(event: Event): void {
    const newSlot = new TimeSchedule(LocalTime.MIN.toJSON(), LocalTime.MAX.toJSON(), 1.0)

    this.showTimeSlotDetail(newSlot).subscribe((slot) => {
      if (slot) {
        const current = this.timeSlots
        this.timeSlots = [...current, slot]
      }
    })
  }

  public editTimeSlot(slot: TimeSchedule): void {
    this.showTimeSlotDetail(slot).subscribe((updatedSlot) => {
      if (updatedSlot) {
        if (updatedSlot.performance > 0) {
          this.timeSlots = [...this.timeSlots.filter((s) => s !== slot), updatedSlot]
        } else {
          this.timeSlots = [...this.timeSlots.filter((s) => s !== slot)]
        }
      }
    })
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private sortByStartingTime(slots: TimeSchedule[]): TimeSchedule[] {
    return slots.sort((s1, s2) => s1.start.localeCompare(s2.start))
  }

  private showTimeSlotDetail(slot: TimeSchedule): Observable<TimeSchedule> {
    return this.matDialog
      .open(TimeScheduleSlotDialogComponent, {
        minWidth: '50vw',
        height: '40vh',
        data: <IScheduleTimeSlotDialogOptions>{
          slot: slot,
        },
      })
      .afterClosed()
  }
}
